

import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function PeptideTherapyPage() {
  const handleConsultationClick = () => {
    window.REACT_APP_NAVIGATE('/consultation');
  };

  const peptideTypes = [
    {
      name: 'BPC-157',
      description: 'Wound healing, tissue and cell regeneration, gut health, and joint repair',
      benefits: ['Accelerated wound healing', 'Tissue regeneration', 'Gut health improvement', 'Joint repair and recovery']
    },
    {
      name: 'CJC-1295',
      description: 'Muscle growth, fat loss, and increased growth hormone production',
      benefits: ['Enhanced muscle growth', 'Effective fat loss', 'Increased GH production', 'Improved body composition']
    },
    {
      name: 'Ipamorelin',
      description: 'Stimulates growth hormone release, often combined with CJC-1295 for muscle growth and anti-aging benefits',
      benefits: ['Natural GH stimulation', 'Muscle development', 'Anti-aging effects', 'Enhanced recovery']
    },
    {
      name: 'AOD-9604',
      description: 'Fat loss and metabolism stimulation',
      benefits: ['Targeted fat reduction', 'Metabolism boost', 'Body recomposition', 'Weight management']
    },
    {
      name: 'Sermorelin',
      description: 'Supports growth hormone production, which can improve sleep, skin, and overall wellness',
      benefits: ['Better sleep quality', 'Improved skin health', 'Enhanced wellness', 'Natural GH support']
    },
    {
      name: 'Thymosin Alpha-1',
      description: 'Boosts and regulates the immune system',
      benefits: ['Immune system boost', 'Immune regulation', 'Enhanced resistance', 'Overall health support']
    },
    {
      name: 'TB-500',
      description: 'Promotes tissue healing and repair, inflammation reduction, and athletic recovery',
      benefits: ['Tissue healing', 'Inflammation reduction', 'Athletic recovery', 'Injury rehabilitation']
    },
    {
      name: 'GHK-Cu',
      description: 'Skin rejuvenation, collagen production, and wound healing',
      benefits: ['Skin rejuvenation', 'Collagen synthesis', 'Wound healing', 'Anti-aging effects']
    },
    {
      name: 'PT-141',
      description: 'Addresses issues like erectile dysfunction and low libido',
      benefits: ['Enhanced libido', 'Improved sexual function', 'Increased desire', 'Better intimacy']
    },
    {
      name: 'Tesamorelin',
      description: 'Targets and reduces visceral fat, often used for body recomposition',
      benefits: ['Visceral fat reduction', 'Body recomposition', 'Improved metabolism', 'Enhanced physique']
    },
    {
      name: 'MK-677',
      description: 'Promotes muscle growth, fat metabolism, and improves sleep and body composition',
      benefits: ['Muscle growth', 'Fat metabolism', 'Better sleep', 'Improved composition']
    },
    {
      name: 'Collagen Peptides',
      description: 'Strengthens bones, hair, and nails; improves skin elasticity',
      benefits: ['Stronger bones', 'Healthier hair & nails', 'Improved skin elasticity', 'Overall vitality']
    }
  ];

  const protocols = [
    {
      name: 'GLOW Protocol',
      description: 'Comprehensive skin rejuvenation and anti-aging peptide combination',
      peptides: ['GHK-Cu', 'Collagen Peptides', 'BPC-157'],
      benefits: ['Radiant skin', 'Collagen boost', 'Cellular repair', 'Youthful appearance']
    },
    {
      name: 'FOX 04 Protocol',
      description: 'Advanced life extension and longevity optimization program',
      peptides: ['CJC-1295', 'Ipamorelin', 'Sermorelin', 'Thymosin Alpha-1'],
      benefits: ['Life extension', 'Longevity optimization', 'Enhanced vitality', 'Cellular regeneration']
    }
  ];

  const benefits = [
    {
      icon: 'ri-dna-line',
      title: 'Cellular Regeneration',
      description: 'Stimulate your body\'s natural healing and regeneration processes at the cellular level'
    },
    {
      icon: 'ri-speed-up-line',
      title: 'Metabolism Boost',
      description: 'Enhance metabolic function for improved energy and weight management'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Muscle Recovery',
      description: 'Accelerate muscle repair and reduce recovery time after exercise'
    },
    {
      icon: 'ri-shield-line',
      title: 'Immunity Enhancement',
      description: 'Strengthen your immune system for better overall health and disease resistance'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8A4F8A] to-[#C8A2C8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ad6e5f6aa89d0f20cc8c0a78cd406957.jpg)'
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">
              Advanced <span className="text-[#F0F0F0]">Peptide Therapy</span>
            </h1>
            <p className="text-xl mb-8 text-[#F0F0F0]">
              Harness the power of cutting-edge peptide science to optimize your health, 
              enhance recovery, and unlock your body's natural healing potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleConsultationClick}>
                <i className="ri-calendar-line mr-2"></i>
                Schedule Consultation
              </Button>
              <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]" onClick={() => window.open('tel:202-269-6600', '_self')}>
                <i className="ri-phone-line mr-2"></i>
                Call 202-269-6600
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Why Choose <span className="text-[#8A4F8A]">Peptide Therapy</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Experience the transformative power of personalized peptide treatments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-[#B0B0B0]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Protocols Section */}
      <section className="py-16 bg-gradient-to-b from-[#F0F0F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Specialized <span className="text-[#8A4F8A]">Protocols</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Advanced peptide combinations designed for specific health goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {protocols.map((protocol, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-black mb-4">{protocol.name}</h3>
                <p className="text-[#B0B0B0] mb-6">{protocol.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-black mb-3">Included Peptides:</h4>
                  <div className="flex flex-wrap gap-2">
                    {protocol.peptides.map((peptide, idx) => (
                      <span key={idx} className="text-sm bg-[#C8A2C8]/20 text-[#8A4F8A] px-3 py-1 rounded-full">
                        {peptide}
                      </span>
                    ))}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {protocol.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center text-black">
                      <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                      {benefit}
                    </li>
                  ))}
                </ul>

                <Button className="w-full" onClick={handleConsultationClick}>
                  <i className="ri-calendar-line mr-2"></i>
                  Book Free Consultation
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Peptides Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Individual <span className="text-[#8A4F8A]">Peptide Treatments</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Targeted peptide therapies for specific health concerns
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {peptideTypes.map((peptide, index) => (
              <div key={index} className="bg-gradient-to-b from-[#F0F0F0] to-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">{peptide.name}</h3>
                <p className="text-[#B0B0B0] mb-4 text-sm">{peptide.description}</p>
                
                <ul className="space-y-2">
                  {peptide.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center text-black text-sm">
                      <i className="ri-check-line text-[#8A4F8A] mr-2"></i>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-[#F0F0F0] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">
            Custom <span className="text-[#8A4F8A]">Pricing</span>
          </h2>
          <p className="text-xl text-[#B0B0B0] mb-8">
            Pricing is completely customized based on the specific peptides needed to address your individual health concerns and goals.
          </p>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <i className="ri-user-heart-line text-4xl text-[#8A4F8A] mr-4"></i>
              <div className="text-left">
                <h3 className="text-2xl font-bold text-black">Personalized Treatment Plans</h3>
                <p className="text-[#B0B0B0]">Every protocol is tailored to your unique needs</p>
              </div>
            </div>
            
            <ul className="text-left space-y-3 mb-8">
              <li className="flex items-center text-black">
                <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                Comprehensive health assessment and consultation
              </li>
              <li className="flex items-center text-black">
                <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                Custom peptide selection based on your goals
              </li>
              <li className="flex items-center text-black">
                <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                Ongoing monitoring and protocol adjustments
              </li>
              <li className="flex items-center text-black">
                <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                Professional medical supervision throughout treatment
              </li>
            </ul>

            <Button size="lg" className="w-full" onClick={handleConsultationClick}>
              <i className="ri-calculator-line mr-2"></i>
              Get Your Custom Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Your <span className="text-[#8A4F8A]">Treatment Journey</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Comprehensive Assessment', description: 'Detailed health evaluation and goal setting' },
              { step: '2', title: 'Personalized Protocol', description: 'Custom peptide plan designed for your needs' },
              { step: '3', title: 'Treatment Initiation', description: 'Begin your peptide therapy with medical supervision' },
              { step: '4', title: 'Ongoing Monitoring', description: 'Regular follow-ups and protocol adjustments' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{item.title}</h3>
                <p className="text-[#B0B0B0] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#8A4F8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Optimize Your Health?</h2>
          <p className="text-xl mb-8 text-[#F0F0F0]">
            Discover the power of personalized peptide therapy
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleConsultationClick}>
              <i className="ri-calendar-line mr-2"></i>
              Schedule Consultation
            </Button>
            <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]" onClick={() => window.open('tel:202-269-6600', '_self')}>
              <i className="ri-phone-line mr-2"></i>
              Call 202-269-6600
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
